import { useMemo } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { noticesV2TargetWithFilters } from "components/notices_v2/noticesV2TargetWithFilters";
import { FIND_OPEN_TENDERS_RELEVANCY_COLUMN_FIELDS } from "components/notices_v2/NoticeTableV2";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import {
  StandardViewName,
  useStandardViewTarget,
} from "lib/hooks/api/notices/views/useStandardViewTarget";
import { useTeam } from "lib/hooks/api/teams/useTeam";
import { EventData, EventNames, useTracking } from "lib/tracking";

const HELP_URL = "https://help.stotles.com/faqs#how-to-build-pipeline-with-bids";

type BidPipelineEmptyStateProps = {
  eventData: EventData;
};

function BidPipelineEmptyState({ eventData }: BidPipelineEmptyStateProps) {
  const { logEvent } = useTracking();
  const isRelevancyNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { data: team, isLoading: isTeamLoading } = useTeam({ enabled: isRelevancyNewUsers });
  const { target: standardViewTarget, isLoading: isStandardViewLoading } = useStandardViewTarget(
    StandardViewName.Open,
    { enabled: !isRelevancyNewUsers },
  );

  const relevancyNoticesV2Target = useMemo(() => {
    const topicFromProfile = team?.metadata?.defaultSearchTopic?.trim() ?? "";
    return noticesV2TargetWithFilters(
      {
        topicSearch: topicFromProfile,
        stage: ["PIPELINE", "PRE_TENDER_V2", "OPEN_TENDER"],
      },
      FIND_OPEN_TENDERS_RELEVANCY_COLUMN_FIELDS,
    );
  }, [team?.metadata?.defaultSearchTopic]);

  const findTendersTarget = isRelevancyNewUsers ? relevancyNoticesV2Target : standardViewTarget;
  const isLoading = isRelevancyNewUsers ? isTeamLoading : isStandardViewLoading;

  return (
    <EmptyState
      type="startingState"
      heading="Your Bid pipeline"
      description={<Description>Create your first Bid from a tender</Description>}
      width="100%"
      size="large"
      action={
        <Flex gap={8} flexWrap="wrap" justifyContent="center">
          <Button as="a" {...findTendersTarget} loading={isLoading}>
            Find tenders
          </Button>
          <Button
            as="a"
            href={HELP_URL}
            buttonType="secondary"
            onClick={() =>
              logEvent(EventNames.clickedHelpCentre, {
                ...eventData,
                "Destination url": HELP_URL,
              })
            }
          >
            Learn more
          </Button>
        </Flex>
      }
    />
  );
}

const Description = styled(Flex)({
  maxWidth: 400,
});

export default BidPipelineEmptyState;
